import { DEFAULT_PAGE_SIZE, USERS_SORT_FIELDS } from './reports.constants';

/**
 * State management for the Users tab of /reports (plus the shared tab and
 * side-panel state). The Transactions tab has its own pair —
 * `reports-transactions.delegate.js` / `reports-transactions.controller.js` —
 * composed alongside this one in `ReportsController`.
 */
class ReportsDelegate {
    /**
     * Design-system `Table` `onChange` for the users tab — receives the full
     * antd signature so the sort is not discarded. Pagination re-fetches the
     * requested page; a sort change (`extra.action === 'sort'`) stores the
     * sort, mirrors it into the URL and reloads from page 1 — sorting is
     * server-side over the whole filtered set.
     *
     * @param {object} pagination
     * @param {object} filters antd column filters (unused)
     * @param {{ columnKey?: string, field?: string, order?: 'ascend'|'descend'|null }} sorter
     * @param {{ action: 'paginate'|'sort'|'filter' }} extra
     */
    handleUsersTableChange(pagination, filters, sorter, extra) {
        if (extra?.action === 'sort') {
            const by = sorter?.columnKey || sorter?.field || null;
            const nextSort = by && sorter?.order && USERS_SORT_FIELDS.includes(by)
                ? { by, dir: sorter.order === 'ascend' ? 'asc' : 'desc' }
                : null;
            this.setUsersSort(nextSort);
            return;
        }
        const page = pagination?.current  || 1;
        const perPage = pagination?.pageSize || DEFAULT_PAGE_SIZE;
        if (typeof this.loadUsers === 'function') {
            this.loadUsers(page, perPage);
        }
    }

    async handleTimeFrameChange(timeFrame, { apply = false } = {}) {
        const nextFilters = { ...this.state.filters, timeFrame };
        this.setState({ filters: nextFilters });
        if (apply) {
            await this.applyFilters(nextFilters);
        }
    }

    async handleDateRangeChange(dateRange) {
        const nextFilters = { ...this.state.filters, dateRange };
        this.setState({ filters: nextFilters });
    }

    async handleFilterChange(name, value, { apply = false } = {}) {
        const nextFilters = { ...this.state.filters, [name]: value };
        this.setState({ filters: nextFilters });
        if (apply) {
            await this.applyFilters(nextFilters);
        }
    }

    setActiveTab(tab) {
        this.setState({ activeTab: tab });
    }

    /**
     * Store the per-user sync detail state (`idle | loading | ready | error`)
     * for the panel. Replaces the old per-row report cache: the table's
     * default render reads the bulk rollup off the row and needs none of this.
     *
     * @param {string} clientId
     * @param {{ status: string, report?: object|null, error?: string|null } | null} detail null clears
     */
    setSyncDetail(clientId, detail) {
        // Functional updater: two detail results landing in the same React
        // batch must not overwrite each other (a lost `ready` would leave a
        // client stuck at `loading`, which has no Retry).
        this.setState((prev) => {
            const next = { ...(prev.syncDetailByClientId || {}) };
            if (detail == null) delete next[clientId];
            else next[clientId] = detail;
            return { syncDetailByClientId: next };
        });
    }

    /**
     * Patch one row's bulk rollup (`record.syncStatus`) in place — used after
     * an admin sync action, where the forced detail fetch already carries the
     * new state, so the page is not refetched just to refresh one pill.
     *
     * @param {string} clientId
     * @param {object} patch fields of the `syncStatus` DTO to overwrite
     */
    setUserSyncRollup(clientId, patch) {
        const apply = (rows) => (rows || []).map((row) => (
            row?.id === clientId && row.syncStatus !== undefined
                ? { ...row, syncStatus: { ...(row.syncStatus || {}), ...patch } }
                : row
        ));
        this.setState((prev) => ({ users: apply(prev.users), usersRows: apply(prev.usersRows) }));
    }

    setSsoLoading(clientId, isLoading) {
        const { ssoLoading } = this.state;
        const next = { ...(ssoLoading || {}) };
        if (isLoading) next[clientId] = true;
        else delete next[clientId];
        this.setState({ ssoLoading: next });
    }

    setActionLoading(clientId, action, isLoading) {
        const key = `${action}Loading`;
        const current = this.state[key] || {};
        const next = { ...current };
        if (isLoading) next[clientId] = true;
        else delete next[clientId];
        this.setState({ [key]: next });
    }

    /**
     * Optimistic override for one member's restricted state, read by
     * `isUserRestricted` ahead of the row's own `kpiUserStatus`.
     *
     * Stores an explicit boolean rather than deleting on false. Deleting made
     * optimistic RESTORE impossible: the key vanished, the tri-state
     * `hasOwnProperty` check in isUserRestricted fell through to the row, and
     * the row still said `kpiUserStatus === 'inactive'` — so restoring access
     * looked like it had not happened until `loadUsers()` came back, and looked
     * permanently broken if that refetch failed.
     */
    setUserRestricted(clientId, restricted) {
        const { restrictedClientIds } = this.state;
        this.setState({
            restrictedClientIds: { ...(restrictedClientIds || {}), [clientId]: !!restricted },
        });
    }

    /**
     * Selects the active row in the users table and loads that user's sync
     * detail report (idempotent if already loaded). Clicking the selected
     * row again deselects to collapse the panel — toggle behaviour that
     * matches the mobile design.
     *
     * @param {string} clientId
     */
    selectUser(clientId) {
        if (!clientId) return;
        const isSame = this.state.selectedUserId === clientId;
        this.setState({ selectedUserId: isSame ? null : clientId });
        if (!isSame && typeof this.loadSyncDetailForUser === 'function') {
            this.loadSyncDetailForUser(clientId);
        }
    }

    clearSelectedUser() {
        this.setState({ selectedUserId: null });
    }

    /**
     * Re-fetches the active dataset at the current page/perPage. Wired to
     * the Refresh Data button in the page header and to the row-level
     * "Retry" when the bulk sync rollup was unavailable. Does not touch
     * `selectedUserId` so the side panel keeps its current selection.
     */
    async refreshData() {
        // A failed catalog load is retried with the data, so the notice's Retry
        // covers both (memoised on success, so this is free afterwards).
        if (typeof this.loadSyncStateCatalog === 'function') this.loadSyncStateCatalog();
        if (this.state.activeTab === 'users') {
            if (typeof this.loadUsers === 'function') {
                await this.loadUsers(
                    this.state.usersPageInfo?.current,
                    this.state.usersPageInfo?.perPage,
                );
            }
            if (typeof this.loadFunnelMetrics === 'function') await this.loadFunnelMetrics();
        } else if (typeof this.loadTransactions === 'function') {
            // Transactions live in ReportsTransactionsController; current page,
            // filters, sort and search are all read from state there.
            await this.loadTransactions();
        }
    }

    /**
     * Enrollment funnel section state in one shot (fetching and branching
     * live in `ReportsController.loadFunnelMetrics`). No cards renders the
     * error panel; `error.reason` (FUNNEL_ERROR) says why.
     *
     * @param {{ cards?: Array, range?: Object|null, optedIn?: Object|null, error?: { reason: string }|null }} next
     */
    setFunnel({ cards = [], range = null, optedIn = null, error = null } = {}) {
        this.setState({
            funnel: cards.length ? cards : null,
            funnelRange: range,
            funnelOptedIn: optedIn,
            funnelError: error,
        });
    }

    setFunnelLoading(isLoading) {
        this.setState({ funnelLoading: Boolean(isLoading) });
    }
}

export default ReportsDelegate;
